// VERIFY: new file — React auth context provider
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  AuthUser,
  LoginResponse,
  login as apiLogin,
  getToken,
  getStoredUser,
  setAuth,
  clearAuth,
  installAxiosInterceptor,
  setOnUnauthorized,
} from '../api/auth';

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser);
  const [token, setToken] = useState<string | null>(getToken);

  // Install axios interceptor once
  useEffect(() => {
    installAxiosInterceptor();
    setOnUnauthorized(() => {
      clearAuth();
      setUser(null);
      setToken(null);
    });
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res: LoginResponse = await apiLogin(username, password);
    const authUser: AuthUser = {
      user_id: res.user_id,
      username: res.username,
      display_name: res.display_name,
    };
    setAuth(res.token, authUser);
    setToken(res.token);
    setUser(authUser);
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    setToken(null);
    setUser(null);
  }, []);

  const value: AuthContextValue = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token && !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
